import React from 'react';
import { useTranslation } from 'react-i18next';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { getScheduleTimelineSummary } from '../scheduleTimeline';
import { formatScheduleDate, toScheduleDateOnly } from '../scheduleChangeDates';
import useStyles from './styles';
const KEY_PREFIX = 'subscriptions.scheduleChange.cancel.timeline';
const NODE = {
  started: 'started',
  today: 'today',
  regularRun: 'regularRun',
  effective: 'effective',
  final: 'final',
  placeholder: 'placeholder'
};
const NODE_RANK = {
  [NODE.started]: 0,
  [NODE.today]: 1,
  [NODE.regularRun]: 2,
  [NODE.effective]: 3,
  [NODE.final]: 4,
  [NODE.placeholder]: 5
};
const COLLAPSED_NODES = new Set([NODE.regularRun, NODE.placeholder]);
const formatMoney = amount => "$".concat(amount.toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}));
export const CancellationTimeline = _ref => {
  let startDate = _ref.startDate,
    effectiveDate = _ref.effectiveDate,
    timeline = _ref.timeline,
    _ref$isLoading = _ref.isLoading,
    isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading;
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const summary = getScheduleTimelineSummary(timeline !== null && timeline !== void 0 ? timeline : []);
  const today = toScheduleDateOnly(new Date());
  const nodes = [];
  if (startDate) {
    nodes.push({
      node: NODE.started,
      date: startDate,
      title: t("".concat(KEY_PREFIX, ".subscription_started")),
      captions: [formatScheduleDate(startDate)],
      dotClass: classes.dotMuted
    });
  }
  nodes.push({
    node: NODE.today,
    date: today,
    title: t("".concat(KEY_PREFIX, ".today")),
    captions: [formatScheduleDate(today)],
    dotClass: classes.dotToday
  });
  if (!effectiveDate) {
    nodes.push({
      node: NODE.placeholder,
      date: null,
      title: t("".concat(KEY_PREFIX, ".placeholder")),
      captions: [],
      dotClass: classes.dotPlaceholder,
      isOutlinedDot: true,
      isMutedTitle: true
    });
  } else {
    const regularRun = summary.regularRun,
      finalNode = summary.finalNode;
    if (regularRun.count > 0) {
      var _regularRun$firstDate;
      nodes.push({
        node: NODE.regularRun,
        date: (_regularRun$firstDate = regularRun.firstDate) !== null && _regularRun$firstDate !== void 0 ? _regularRun$firstDate : null,
        title: t("".concat(KEY_PREFIX, ".regular_invoices"), {
          count: regularRun.count
        }),
        captions: [regularRun.firstDate === regularRun.lastDate ? t("".concat(KEY_PREFIX, ".regular_single"), {
          date: formatScheduleDate(regularRun.firstDate)
        }) : t("".concat(KEY_PREFIX, ".regular_range"), {
          from: formatScheduleDate(regularRun.firstDate),
          to: formatScheduleDate(regularRun.lastDate)
        })],
        dotClass: classes.dotMuted
      });
    }
    nodes.push({
      node: NODE.effective,
      date: effectiveDate,
      title: t("".concat(KEY_PREFIX, ".effective")),
      captions: [t("".concat(KEY_PREFIX, ".effective_caption"), {
        date: formatScheduleDate(effectiveDate)
      })],
      dotClass: classes.dotEffective
    });
    if (finalNode) {
      const chargedAmount = summary.hasCharge ? finalNode.amount : undefined;
      const coveredCaption = finalNode.coveredMonthCount === 1 ? t("".concat(KEY_PREFIX, ".final_covers_single"), {
        date: formatScheduleDate(finalNode.firstCoveredMonth)
      }) : t("".concat(KEY_PREFIX, ".final_covers"), {
        count: finalNode.coveredMonthCount,
        from: formatScheduleDate(finalNode.firstCoveredMonth),
        to: formatScheduleDate(finalNode.lastCoveredMonth)
      });
      nodes.push({
        node: NODE.final,
        date: finalNode.date,
        title: chargedAmount === undefined ? t("".concat(KEY_PREFIX, ".final_invoice")) : t("".concat(KEY_PREFIX, ".final_invoice_charge"), {
          amount: formatMoney(chargedAmount)
        }),
        captions: chargedAmount !== undefined && finalNode.coveredMonthCount > 0 ? [formatScheduleDate(finalNode.date), coveredCaption] : [formatScheduleDate(finalNode.date)],
        dotClass: chargedAmount === undefined ? classes.dotMuted : classes.dotCharge
      });
    }
  }
  const ordered = [...nodes].sort((left, right) => {
    if (!left.date) return 1;
    if (!right.date) return -1;
    if (left.date !== right.date) return left.date < right.date ? -1 : 1;
    return NODE_RANK[left.node] - NODE_RANK[right.node];
  });
  const isCollapsedSegment = (from, to) => !!from && COLLAPSED_NODES.has(from.node) || !!to && COLLAPSED_NODES.has(to.node);
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.root
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.title
  }, t("".concat(KEY_PREFIX, ".title"))), isLoading && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    className: classes.updating
  }, t("".concat(KEY_PREFIX, ".updating"))), /*#__PURE__*/React.createElement(Timeline, {
    className: classes.timeline
  }, ordered.map((item, index) => {
    var _item$dotClass;
    return /*#__PURE__*/React.createElement(TimelineItem, {
      key: item.node
    }, /*#__PURE__*/React.createElement(TimelineSeparator, null, /*#__PURE__*/React.createElement(TimelineDot, {
      className: "".concat(classes.dot, " ").concat((_item$dotClass = item.dotClass) !== null && _item$dotClass !== void 0 ? _item$dotClass : ''),
      variant: item.isOutlinedDot ? 'outlined' : 'filled'
    }), index < ordered.length - 1 && /*#__PURE__*/React.createElement(TimelineConnector, {
      className: isCollapsedSegment(item, ordered[index + 1]) ? classes.connectorDashed : classes.connector
    })), /*#__PURE__*/React.createElement(TimelineContent, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: item.isMutedTitle ? classes.itemTitlePlaceholder : classes.itemTitle
    }, item.title), item.captions.map(caption => /*#__PURE__*/React.createElement(Typography, {
      key: caption,
      variant: "caption",
      className: classes.itemCaption
    }, caption))));
  })));
};